import React, { useState } from 'react'
import styles from './unitAdd.jcss'
import EditText from '../../../blocks.simple/editText/editText'
import translate from '../../../core/translate'
import Button from '../../../blocks.simple/button/button'
import CatalogSelectDevices from '../../../blocks.app/catalog/_select/_devices/catalog_select_devices'
import Icon from '../../../blocks.simple/icon/icon'
import ModalWithBackground from 'blocks.simple/modalWithBackground/modalWithBackground'
import { api } from 'core/api/ConnectionManager'

type SelectedItem = {
    id: number
    name: string
}

type CheckDisplaysData = {
    floorName: string
    unitName: string
    digitalSignageName: string
}

export const UnitAdd = ({ unit, onChange, onClose, onSave }: any) => {
    const [confirmData, setConfirmData] = useState<null | {
        selectedItems: SelectedItem[]
        resolve: (value: boolean) => void
    }>(null)
    const [movingDisplays, setMovingDisplays] = useState<CheckDisplaysData[]>([])

    const onBeforeSelect = (selectedItems: SelectedItem[], floorId: number) => {
        return new Promise<boolean>(async (resolve) => {
            const res: CheckDisplaysData[] = await api.send('getUsedDsNotificationData', {
                digitalSignageIds: selectedItems.map((i) => i.id),
                notificationFloorId: floorId,
            })

            const usedDSData = res.filter((ds) => ds.floorName && ds.unitName)

            if (usedDSData?.length > 0) {
                setConfirmData({ selectedItems, resolve })
                setMovingDisplays(res)
            } else {
                resolve(true)
            }
        })
    }

    const onFloorNameChange = (value: string, id: number) => {
        onChange(
            'floorList',
            unit.floorList.map((floor: any) => (floor.id === id ? { ...floor, name: value } : floor))
        )
    }

    const onDSListChange = (list: any, index: number) => {
        const idList = list.map((display: { id: number }) => display.id)
        onChange(
            'floorList',
            unit.floorList.map((item: any, i: number) => {
                if (i === index) {
                    return { ...item, digitalSignages: list }
                } else if (item.digitalSignages.some((ds: any) => idList.includes(ds.id))) {
                    return {
                        ...item,
                        digitalSignages: item.digitalSignages.filter((ds: any) => !idList.includes(ds.id)),
                    }
                }

                return item
            })
        )
    }

    const onFloorAdd = () => {
        onChange('floorList', [...unit.floorList, { id: Math.random(), name: '', digitalSignages: [], groups: [] }])
    }

    const onFloorDelete = (index: number) => {
        onChange(
            'floorList',
            unit.floorList.filter((_floor: any, i: number) => i !== index)
        )
    }

    const isValid = () => {
        return (
            !unit.unitName ||
            !unit.officeCode ||
            !unit.unitCode ||
            !unit.floorList.length ||
            unit.floorList.some((floor: any) => !floor.name)
        )
    }

    if (!unit) return null

    return (
        <>
            <div className={styles.unitAdd}>
                <div className={styles.inner}>
                    <div className={styles.field}>
                        <EditText
                            value={unit.unitName}
                            onChange={(v: string) => onChange('unitName', v)}
                            containerClass={styles.separator}
                            className={styles.input}
                            label={translate('unitName')}
                        />
                    </div>
                    <div className={styles.field}>
                        <EditText
                            value={unit.officeCode}
                            onChange={(v: string) => onChange('officeCode', v)}
                            containerClass={styles.separator}
                            className={styles.input}
                            label={translate('officeCode')}
                        />
                    </div>
                    <div className={styles.field}>
                        <EditText
                            value={unit.unitCode}
                            onChange={(v: string) => onChange('unitCode', v)}
                            containerClass={styles.separator}
                            className={styles.input}
                            label={translate('unitCode')}
                        />
                    </div>

                    <div className={styles.field}>
                        <div className={styles.floorTitle}>{translate('floors')}</div>
                        {unit.floorList.map((floor: any, index: number) => (
                            <div className={styles.floor} key={floor.id}>
                                <div className={styles.deleteFloor} onClick={() => onFloorDelete(index)}>
                                    <Icon type="delete" size={14} tooltip={{ title: translate('deleteFloor') }} />
                                </div>
                                <div className={styles.deleteDevices} onClick={() => onDSListChange([], index)}>
                                    <Icon type="delete" size={14} tooltip={{ title: translate('deleteAllDevices') }} />
                                </div>

                                <EditText
                                    value={floor.name}
                                    onChange={(v: string) => onFloorNameChange(v, floor.id)}
                                    containerClass={styles.separator}
                                    className={styles.input}
                                    label={translate('floorNumber')}
                                />

                                <div className={styles.devicesTitle}>{translate('selectedDevices')}</div>

                                {floor.digitalSignages.map((device: any) => (
                                    <div className={styles.device} key={device.id}>
                                        <Icon
                                            type={device.type === 'digitalSignage' ? 'display' : 'folder'}
                                            size={14}
                                        />
                                        <div className={styles.deviceName}>{device.name}</div>
                                    </div>
                                ))}

                                <CatalogSelectDevices
                                    showButton
                                    selectedItems={floor.digitalSignages}
                                    onSelectInDetails={(digitalSignages: any) => onDSListChange(digitalSignages, index)}
                                    disableShowDeviceList
                                    onBeforeSelect={(e) => {
                                        return onBeforeSelect(e, floor.id)
                                    }}
                                />
                            </div>
                        ))}
                        <Button mod="fill" className={styles.button} onClick={onFloorAdd}>
                            {translate('addFloor')}
                        </Button>
                    </div>

                    <div className={styles.controls}>
                        <Button mod="withBorder" className={styles.button} onClick={onClose}>
                            {translate('cancel')}
                        </Button>
                        <Button mod="fill" className={styles.button} onClick={onSave} disabled={isValid()}>
                            {translate(unit.id ? 'save' : 'create')}
                        </Button>
                    </div>
                </div>
            </div>

            <ModalWithBackground
                isOpen={!!confirmData}
                onClose={() => {
                    confirmData?.resolve(false)
                    setConfirmData(null)
                }}
                title={translate('confirmDisplaysMove')}
            >
                <div className={styles.confirmDisplaysMoveWrapper}>
                    <div className={styles.confirmDisplaysMoveText}>{translate('displaysUsedInOtherUnits')}</div>

                    <div className={styles.confirmDisplaysMoveTable}>
                        <div className={styles.tableRow + ' ' + styles.tableHeader}>
                            <div className={styles.tableCell}>{translate('device')}</div>
                            <div className={styles.tableCell}>{translate('dsUnit')}</div>
                            <div className={styles.tableCell}>{translate('floor')}</div>
                        </div>

                        {movingDisplays.map((display, index) => (
                            <div key={index} className={styles.tableRow}>
                                <div className={styles.tableCell}>{display.digitalSignageName}</div>
                                <div className={styles.tableCell}>{display.unitName}</div>
                                <div className={styles.tableCell}>{display.floorName}</div>
                            </div>
                        ))}
                    </div>

                    <div className={styles.buttonRow}>
                        <Button
                            mod="withBorder"
                            onClick={() => {
                                confirmData?.resolve(false)
                                setConfirmData(null)
                            }}
                        >
                            {translate('cancel')}
                        </Button>
                        <Button
                            mod="fill"
                            onClick={() => {
                                confirmData?.resolve(true)
                                setConfirmData(null)
                            }}
                        >
                            {translate('submit')}
                        </Button>
                    </div>
                </div>
            </ModalWithBackground>
        </>
    )
}
